/**
 * 63.1d step 4 (lane F): a ride — the transport's half. A `ride` order (a task naming a transport of the squad's side and a
 * place; core/orderForms.ts validates it) is obeyed by the squad's brains (lane B's half, ai/actionBot.ts): they walk to
 * the transport, get in (the seat intent, core/seats.ts: E takes the free seat whose entry is nearest), its driver drives
 * to the place, and everyone gets out within reach of it. These are the readers that brain needs, pure over the state:
 *   - `rideSeatFor`: the seat a member should go for — its place in `boardingOrder` (the driver's, the gun seats, the bays; null: none left);
 *   - `rideEntry`: where on the ground that seat is boarded (its measured entry, the vehicle's own frame);
 *   - `rideArrived`: the transport stands within `RIDE_ARRIVE` people of the order's place — time to get out.
 */
import type { GameState, SeatState, StructureState, Vec3 } from '../types';
import type { SquadOrder } from '../types/inputs';
import { seatPoint, seatTaken, seatUnit } from './seats';
import { seatArmed } from './body';
import { orderPlace } from './orderForms';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';

/** How near the place the transport must stand for its riders to get out (people). */
export const RIDE_ARRIVE = 8;

/** The seat `unitId` should go for in `transport`: its own if aboard, else the `rank`-th of `boardingOrder` (the squad's members on foot in id order); null when none is left. */
export function rideSeatFor(transport: Pick<StructureState, 'seats' | 'disabled'>, unitId: number, rank = 0): number | null {
  const seats = transport.seats ?? [];
  const own = seats.findIndex(s => seatUnit(s) === unitId);
  if (own >= 0) return own;
  return boardingOrder(seats, undefined, !!transport.disabled)[rank] ?? null;
}

/**
 * 63.1h (the owner: "bots should preferentially get in the gunners seat when entering the apc if seat available", then
 * "When ordered, driver first preference."): the free seats of a vehicle in the order a squad told to board it takes them
 * — `first` (a seat a human named) if free, the driver's, the gun seats, then the bays. A squad's members on foot take
 * them one each, in id order (`rank`), so the second goes for the cupola's .50, not the driver's door the first is at.
 * 73.7a (`noDriver`: the hull mobility-killed, StructureState.disabled): its driver's seat is no seat to take.
 */
export function boardingOrder(seats: readonly SeatState[], first?: number, noDriver = false): number[] {
  const free = seats.map((_, k) => k).filter(k => !seatTaken(seats[k]) && !(noDriver && seats[k].role === 'driver'));
  const rank = (k: number) => k === first ? 0 : seats[k].role === 'driver' ? 1 : seats[k].mortar ? 2 : seatArmed(seats[k]) ? 3 : 4;   // (69.23: the mortar's crew seat next to the driver's)
  return free.sort((a, b) => rank(a) - rank(b) || a - b);
}

/** Where `seat` of `transport` is boarded, as a surface direction (its measured entry in the vehicle's frame); its own spot for a body without entries. */
export function rideEntry(state: Pick<GameState, 'planets' | 'config'>, transport: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'planetIndex'>, seat: number): Vec3 {
  const planet = state.planets[transport.planetIndex];
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  return (planet ? seatPoint(transport, seat, 'entry', planet.radius, person) : null) ?? transport.surfaceDir;
}

/** Has the ride arrived: the order's transport stands within RIDE_ARRIVE people of its place (false for any other order, or a place that does not resolve). */
export function rideArrived(state: Pick<GameState, 'planets' | 'config' | 'structures' | 'modeState'>, order: SquadOrder): boolean {
  if (order.kind !== 'ride') return false;
  const t = state.structures.find(s => s.id === order.structureId);
  const place = orderPlace(state, order);
  if (!t || !place || place.planetIndex !== t.planetIndex) return false;
  const planet = state.planets[t.planetIndex];
  return !!planet && arcDistance(t.surfaceDir, place.surfaceDir, planet.radius) <= RIDE_ARRIVE * PERSON_BODY_HEIGHT * personSetOf(state).k;
}
